import {gaIdentify, gaPage, gaTrack, initGoogleAnalytics} from '@/utils/tracking/ga'
import {
  initMixpanel,
  mixpanelAlias,
  mixpanelIdentify,
  mixpanelRegister,
  mixpanelReset,
  mixpanelTrack,
  setMixpanelTrackingEnabled,
} from '@/utils/tracking/mixpanel'

type Properties = Record<string, unknown>

export const initAnalytics = () => {
  initMixpanel()
  initGoogleAnalytics()
}

type AnalyticsDebugConfig = {
  // Log every analytics call to the console (dev visibility).
  logToConsole: boolean
  // Whether calls actually reach Mixpanel/GA/GTM. Off = events are swallowed.
  sendToProviders: boolean
}

const debugConfig: AnalyticsDebugConfig = {
  logToConsole: false,
  sendToProviders: true,
}

export const setAnalyticsDebugConfig = (config: Partial<AnalyticsDebugConfig>) => {
  Object.assign(debugConfig, config)
  setMixpanelTrackingEnabled(debugConfig.sendToProviders)
}

const debugLog = (label: string, ...args: unknown[]) => {
  if (debugConfig.logToConsole) {
    // eslint-disable-next-line no-console
    console.log(`%c[analytics] ${label}`, 'color: #7c3aed; font-weight: bold;', ...args)
  }
}

export const analytics = {
  track: (event: string, properties?: Properties): void => {
    debugLog('track', event, properties ?? {})
    if (!debugConfig.sendToProviders) {
      return
    }
    mixpanelTrack(event, properties)
    gaTrack(event, properties)
  },

  identify: (userId: string, traits?: Properties): void => {
    debugLog('identify', userId, traits ?? {})
    if (!debugConfig.sendToProviders) {
      return
    }
    mixpanelIdentify(userId, traits)
    // GA4 direct gets only the non-PII user_id; the GTM dataLayer still receives
    // traits (for enhanced conversions). PII never reaches GA4 itself.
    gaIdentify(userId, traits)
  },

  // Mixpanel-only (GA has no alias concept).
  // Original ID Merge: call once at signup to link pre-signup anonymous activity
  // to the new account.
  alias: (userId: string): void => {
    debugLog('alias', userId)
    if (!debugConfig.sendToProviders) {
      return
    }
    mixpanelAlias(userId)
  },

  page: (pageName: string = 'Page View'): void => {
    debugLog('page', pageName)
    if (!debugConfig.sendToProviders) {
      return
    }
    mixpanelTrack(pageName)
    gaPage({pageName})
  },

  reset: (): void => {
    debugLog('reset')
    if (!debugConfig.sendToProviders) {
      return
    }
    mixpanelReset()
  },

  register: (properties: Properties): void => {
    debugLog('register', properties)
    if (!debugConfig.sendToProviders) {
      return
    }
    mixpanelRegister(properties)
  },
}
